import type {
  DepositChargeConfig,
  InstalmentPlanChargeConfig,
  InstalmentPlanConfig,
  InstalmentRowConfig,
} from "./types";
import { shiftCalendarDays } from "./resolveBalanceDueDate";

/**
 * N-instalment payment plans — the general case of which the legacy 2-leg
 * deposit (`DepositChargeConfig`) is the N=2 degenerate form.
 *
 * Design (see plans/ticketsv2-instalment-plans.md):
 * - A deposit ticket's `chargeConfig` carries EITHER the legacy shape OR
 *   `{ instalmentPlan }`. Nothing ever migrates: `resolveInstalmentPlan`
 *   synthesizes a 2-row plan from legacy configs at read time, so every
 *   consumer sees one shape and legacy tickets keep working untouched.
 * - Row 1 is always the deposit, charged at checkout (`at_checkout`). Rows
 *   2..N are collected later via balance payment links or in person.
 * - Due dates resolve at booking time, uniformly in Europe/London (the
 *   DEV-277 decision: cross-path consistency over absolute tz precision),
 *   through the same DST-safe calendar-day math as the legacy resolver.
 */

export const MAX_INSTALMENT_ROWS = 12;

/** Discriminates the N-instalment `chargeConfig` shape from the legacy 2-leg one. */
export function isInstalmentPlanConfig(
  cfg: unknown
): cfg is InstalmentPlanChargeConfig {
  return (
    !!cfg &&
    typeof cfg === "object" &&
    !!(cfg as any).instalmentPlan &&
    Array.isArray((cfg as any).instalmentPlan.rows)
  );
}

/**
 * Resolve a deposit ticket's `chargeConfig` to an `InstalmentPlanConfig`,
 * whichever shape it carries. Legacy `DepositChargeConfig`s synthesize a
 * 2-row plan:
 *
 *   row 1: the deposit — the config's percentage/fixed value, at_checkout.
 *   row 2: the balance — `amountType: "remainder"` (a fixed deposit's balance
 *          is not expressible as a config constant without the price), due per
 *          `remainderDue`, collected per `remainderCollection`.
 *
 * Mappings mirror the established legacy semantics exactly:
 * - `remainderDue "days_before"` → `days_before_event` + `daysBefore`.
 * - `"on_arrival"`, the removed legacy `"specific_date"`, and any unknown
 *   value → `on_arrival` (same coercion as `resolveBalanceDueDate`; a null
 *   here would leave the row unreminded forever).
 * - Absent `remainderCollection` → `"in_person"` (same as applyDepositToBooking).
 * - `sendReminder` requires an explicit `true` (same as the reminder cron).
 *
 * Returns `null` for null/undefined or a config that is neither shape.
 */
export function resolveInstalmentPlan(
  cfg: DepositChargeConfig | InstalmentPlanChargeConfig | null | undefined
): InstalmentPlanConfig | null {
  if (!cfg || typeof cfg !== "object") return null;
  if (isInstalmentPlanConfig(cfg)) return cfg.instalmentPlan;

  const d = cfg as DepositChargeConfig;
  if (d.depositType !== "percentage" && d.depositType !== "fixed") return null;

  const balanceRow: InstalmentRowConfig =
    (d.remainderDue as string) === "days_before"
      ? {
          label: "Balance",
          amountType: "remainder",
          amountValue: 0,
          dueType: "days_before_event",
          dueDays: d.daysBefore ?? 0,
          collection:
            d.remainderCollection === "payment_link" ? "payment_link" : "in_person",
        }
      : {
          label: "Balance",
          amountType: "remainder",
          amountValue: 0,
          dueType: "on_arrival",
          collection:
            d.remainderCollection === "payment_link" ? "payment_link" : "in_person",
        };

  return {
    rows: [
      {
        label: "Deposit",
        amountType: d.depositType,
        amountValue: d.depositValue,
        dueType: "at_checkout",
        collection: "payment_link",
      },
      balanceRow,
    ],
    sendReminder: d.sendReminder === true,
    ...(typeof d.reminderDaysBefore === "number"
      ? { reminderDaysBefore: d.reminderDaysBefore }
      : {}),
  };
}

/**
 * Materialise an instalment plan's row amounts for one priced line, in pence.
 *
 * - percentage rows: `round(lineContract × pct / 100)`
 * - fixed rows:      `amountValue × unitCount` (fixed values are per unit)
 * - remainder row:   whatever is left
 *
 * Regardless of shape, the LAST row absorbs the exact remainder and every row
 * is clamped to `[0, still-unallocated]` — so `Σ rows === lineContractPence`
 * ALWAYS, even when pricing rules have moved `finalPrice` away from the ticket
 * price a fixed plan was configured against (early rows cap, the tail absorbs).
 * Shared by the server pricing path and (Phase 7) the client preview, so both
 * materialise identical schedules.
 */
export function computeInstalmentRowAmounts(
  rows: InstalmentRowConfig[],
  args: { lineContractPence: number; unitCount?: number }
): number[] {
  const { lineContractPence, unitCount = 1 } = args;
  const n = rows.length;
  if (n === 0) return [];
  const amounts = new Array<number>(n).fill(0);
  let remaining = Math.max(0, lineContractPence);
  for (let i = 0; i < n - 1; i++) {
    const r = rows[i];
    const raw =
      r.amountType === "percentage"
        ? Math.round((lineContractPence * r.amountValue) / 100)
        : r.amountType === "fixed"
          ? r.amountValue * unitCount
          : 0; // a mid-plan "remainder" is rejected by validation; safe default
    const amt = Math.max(0, Math.min(raw, remaining));
    amounts[i] = amt;
    remaining -= amt;
  }
  amounts[n - 1] = remaining;
  return amounts;
}

/**
 * The deposit split of ONE priced deposit line, in pence: what is due now and
 * what is owed later. The single implementation behind both the server engine
 * (`calculateV2BookingPrice`) and the checkout preview (`useV2Pricing`), so
 * the deposit a customer is shown is the deposit they are charged.
 *
 * Everything comes off `lineTotalPence`, the line's ACTUAL contract, never
 * `unitPricePence × units`. The two differ on a TIERED_PRICING line, whose
 * rule prices the whole line and whose unit price is only a rounded average.
 * `units` are the CHARGED units, session-scaled: tickets past a tiered
 * `maxTickets` cap are free and carry no deposit (`PricingResult.chargedQuantity`).
 *
 * - instalment plan: `computeInstalmentRowAmounts` over the line; row 0 is the
 *   deposit, and `rowAmounts` is the whole schedule (Σ === line).
 * - legacy fixed: `depositValue` per charged unit, capped at the line.
 * - legacy percentage: the share of the line. On a uniform line
 *   (`unitPrice × units === lineTotal`, i.e. every non-tiered line) it is
 *   rounded per unit and multiplied back, the figure these tickets have always
 *   been charged. A ladder whose average doesn't divide evenly rounds once
 *   against the line.
 *
 * The deposit is clamped to `[0, line]`, so the balance is never negative.
 */
export function computeLineDepositSplit(
  chargeConfig: DepositChargeConfig | InstalmentPlanChargeConfig,
  line: { lineTotalPence: number; unitPricePence: number; units: number }
): { depositPence: number; remainderPence: number; rowAmounts: number[] | null } {
  const { lineTotalPence, unitPricePence, units } = line;
  if (isInstalmentPlanConfig(chargeConfig)) {
    const rowAmounts = computeInstalmentRowAmounts(chargeConfig.instalmentPlan.rows, {
      lineContractPence: lineTotalPence,
      unitCount: units,
    });
    const depositPence = rowAmounts[0] ?? 0;
    return { depositPence, remainderPence: lineTotalPence - depositPence, rowAmounts };
  }
  const cfg = chargeConfig as DepositChargeConfig;
  const raw =
    cfg.depositType === "percentage"
      ? unitPricePence * units === lineTotalPence
        ? Math.round((unitPricePence * cfg.depositValue) / 100) * units
        : Math.round((lineTotalPence * cfg.depositValue) / 100)
      : cfg.depositValue * units;
  const depositPence = Math.max(0, Math.min(raw, lineTotalPence));
  return { depositPence, remainderPence: lineTotalPence - depositPence, rowAmounts: null };
}

/**
 * How much of a booking's OUTSTANDING cash commission this online leg should
 * carry, in pence.
 *
 * Replaces the previous per-row "carried stamp" machinery. Nothing is stored on
 * the instalment rows and nothing is ever moved between them: each leg simply
 * takes its proportional share of whatever is still outstanding, where
 * outstanding is derived from the payments that have actually succeeded. A leg
 * collected in cash, a cancelled plan, a capped fee, a re-opened link — all of
 * them resolve themselves, because the outstanding figure is unchanged and the
 * remaining legs are re-weighted on their own next pass.
 *
 * `remainingOnlinePence` is the total amount of the online legs still to be
 * charged, INCLUDING this one. When this is the last such leg it takes the whole
 * remainder.
 *
 * Capped so `ownFeePence + carried ≤ legAmountPence` (Stripe rejects a fee above
 * the charge). A truncated remainder needs no spill: it stays outstanding and
 * the next leg picks it up.
 */
export function commissionForLeg(args: {
  outstandingPence: number;
  legAmountPence: number;
  remainingOnlinePence: number;
  ownFeePence: number;
}): number {
  const { outstandingPence, legAmountPence, remainingOnlinePence, ownFeePence } =
    args;
  const outstanding = Math.max(0, Math.round(outstandingPence));
  if (outstanding === 0) return 0;
  const legAmount = Math.max(0, Math.round(legAmountPence));
  // Headroom under Stripe's fee-cannot-exceed-charge rule.
  const headroom = Math.max(0, legAmount - Math.max(0, Math.round(ownFeePence)));
  if (headroom === 0) return 0;

  const remaining = Math.max(0, Math.round(remainingOnlinePence));
  // Last (or only) chargeable leg takes everything still owed; otherwise a
  // proportional share by amount. `>=` because remaining INCLUDES this leg.
  const share =
    remaining <= legAmount || remaining === 0
      ? outstanding
      : Math.floor((outstanding * legAmount) / remaining);

  return Math.min(share, outstanding, headroom);
}

/**
 * Resolve one instalment row's concrete due date.
 *
 * - `at_checkout`         → `purchaseDate` (the row is paid with the checkout charge).
 * - `on_arrival`          → `sessionStart`.
 * - `days_before_event`   → `dueDays` calendar days before `sessionStart`.
 * - `days_after_purchase` → `dueDays` calendar days after `purchaseDate`.
 *
 * Calendar-day arithmetic is DST-safe (`shiftCalendarDays`) and uniformly
 * Europe/London by default. Returns `null` when the rule's anchor date is not
 * available (e.g. `on_arrival` with no session date) — matching the legacy
 * resolver's behaviour, where a null due date means "not chased by the cron".
 */
export function resolveInstalmentDueDate(
  row: Pick<InstalmentRowConfig, "dueType" | "dueDays">,
  args: {
    sessionStart?: Date | null;
    purchaseDate?: Date | null;
    timeZone?: string;
  }
): Date | null {
  const { sessionStart, purchaseDate, timeZone = "Europe/London" } = args;
  switch (row.dueType) {
    case "at_checkout":
      return purchaseDate ?? null;
    case "on_arrival":
      return sessionStart ?? null;
    case "days_before_event":
      return sessionStart
        ? shiftCalendarDays(sessionStart, -(row.dueDays ?? 0), timeZone)
        : null;
    case "days_after_purchase":
      return purchaseDate
        ? shiftCalendarDays(purchaseDate, row.dueDays ?? 0, timeZone)
        : null;
    default:
      return null;
  }
}

/**
 * Validate an instalment plan's shape. Returns a list of human-readable
 * problems — empty means valid. Pure, so the server save guard
 * (CreateTicketTypeV2 / UpdateTicketTypeV2) and the dashboard builder's live
 * validator run the exact same rules.
 *
 * `ticketPricePence` enables the fixed-amount sum check; when omitted (price
 * unknown at call time) that one check is skipped.
 */
export function validateInstalmentPlan(
  plan: InstalmentPlanConfig,
  opts: { ticketPricePence?: number } = {}
): string[] {
  const errors: string[] = [];
  const rows = plan?.rows;

  if (!Array.isArray(rows) || rows.length < 2) {
    return ["An instalment plan needs at least 2 rows (deposit + one instalment)"];
  }
  if (rows.length > MAX_INSTALMENT_ROWS) {
    errors.push(`An instalment plan can have at most ${MAX_INSTALMENT_ROWS} rows`);
  }

  // ── Row 1: the deposit, paid with the checkout charge ──
  const first = rows[0];
  if (first.dueType !== "at_checkout") {
    errors.push('Row 1 is the deposit and must have dueType "at_checkout"');
  }
  if (first.collection !== "payment_link") {
    errors.push('Row 1 is charged at checkout and must have collection "payment_link"');
  }
  if (first.amountType === "remainder") {
    errors.push('Row 1 cannot be the "remainder" row — the deposit must be explicit');
  }

  const last = rows[rows.length - 1];
  const remainderRows = rows.filter((r) => r.amountType === "remainder");
  if (remainderRows.length > 1) {
    errors.push('At most one "remainder" row is allowed');
  }
  if (remainderRows.length === 1 && last.amountType !== "remainder") {
    errors.push('The "remainder" row must be the last row');
  }

  // ── Per-row shape ──
  const explicit = rows.filter((r) => r.amountType !== "remainder");
  const explicitTypes = new Set(explicit.map((r) => r.amountType));
  if (explicitTypes.size > 1) {
    errors.push("All rows must share one amount type (all percentage or all fixed)");
  }

  rows.forEach((row, i) => {
    const n = i + 1;
    if (!["percentage", "fixed", "remainder"].includes(row.amountType)) {
      errors.push(`Row ${n}: unknown amountType "${row.amountType}"`);
      return;
    }
    if (row.amountType !== "remainder") {
      if (!Number.isFinite(row.amountValue) || row.amountValue <= 0) {
        errors.push(`Row ${n}: amount must be greater than 0`);
      }
    }
    if (
      !["at_checkout", "days_before_event", "days_after_purchase", "on_arrival"].includes(
        row.dueType
      )
    ) {
      errors.push(`Row ${n}: unknown dueType "${row.dueType}"`);
      return;
    }
    if (i > 0 && row.dueType === "at_checkout") {
      errors.push(`Row ${n}: only row 1 can be due at checkout`);
    }
    if (row.dueType === "on_arrival" && i !== rows.length - 1) {
      errors.push(`Row ${n}: "on arrival" is only allowed on the final row`);
    }
    if (row.dueType === "days_before_event" || row.dueType === "days_after_purchase") {
      if (!Number.isInteger(row.dueDays) || (row.dueDays as number) < 0) {
        errors.push(`Row ${n}: dueDays must be a whole number of days (0 or more)`);
      }
    }
    if (!["payment_link", "in_person"].includes(row.collection)) {
      errors.push(`Row ${n}: unknown collection "${row.collection}"`);
    }
  });

  // ── Amounts must add up ──
  // NEVER compare float sums with !==/>=: IEEE-754 makes 33.4 + 33.3 + 33.3
  // sum to 99.99999999999999, so a split that is mathematically exactly 100%
  // was rejected with the self-contradicting "must total exactly 100% (got
  // 99.99999999999999%)" — 8.2% of all one-decimal 3-row splits failed this
  // way, and the school had no way to work out what to change. Sum in integer
  // BASIS POINTS (1% = 100bp; two decimals of percent is the finest the
  // builder accepts) so equality is exact. Fixed amounts are pence and get the
  // same rounding for safety (a pounds×100 conversion can also carry float dust).
  const toBp = (v: number) => Math.round(v * 100);
  const explicitSumBp = explicit.reduce(
    (s, r) => s + (Number.isFinite(r.amountValue) ? toBp(r.amountValue) : 0),
    0
  );
  const explicitSumLabel = explicitSumBp / 100;
  const hasRemainder = remainderRows.length === 1;
  if (explicitTypes.size === 1 && explicitTypes.has("percentage")) {
    if (hasRemainder && explicitSumBp >= 10000) {
      errors.push(
        `Percentages before the remainder row must total less than 100% (got ${explicitSumLabel}%)`
      );
    }
    if (!hasRemainder && explicitSumBp !== 10000) {
      errors.push(
        `Percentages must total exactly 100% (got ${explicitSumLabel}%)`
      );
    }
  }
  if (
    explicitTypes.size === 1 &&
    explicitTypes.has("fixed") &&
    typeof opts.ticketPricePence === "number"
  ) {
    const explicitPence = explicit.reduce(
      (s, r) => s + (Number.isFinite(r.amountValue) ? Math.round(r.amountValue) : 0),
      0
    );
    if (hasRemainder && explicitPence >= opts.ticketPricePence) {
      errors.push(
        `Fixed amounts before the remainder row must total less than the ticket price (${explicitPence} of ${opts.ticketPricePence} pence)`
      );
    }
    if (!hasRemainder && explicitPence !== opts.ticketPricePence) {
      errors.push(
        `Fixed amounts must total exactly the ticket price (${explicitPence} of ${opts.ticketPricePence} pence)`
      );
    }
  }

  // ── Due-date order must make sense (checked per anchor type, in row order) ──
  const daysBefore = rows.filter((r) => r.dueType === "days_before_event");
  for (let i = 1; i < daysBefore.length; i++) {
    if ((daysBefore[i].dueDays ?? 0) >= (daysBefore[i - 1].dueDays ?? 0)) {
      errors.push(
        '"Days before event" rows must get closer to the event with each instalment'
      );
      break;
    }
  }
  const daysAfter = rows.filter((r) => r.dueType === "days_after_purchase");
  for (let i = 1; i < daysAfter.length; i++) {
    if ((daysAfter[i].dueDays ?? 0) <= (daysAfter[i - 1].dueDays ?? 0)) {
      errors.push(
        '"Days after purchase" rows must move further from purchase with each instalment'
      );
      break;
    }
  }

  // ── Reminder config ──
  if (
    plan.reminderDaysBefore !== undefined &&
    (!Number.isInteger(plan.reminderDaysBefore) || plan.reminderDaysBefore < 0)
  ) {
    errors.push("reminderDaysBefore must be a whole number of days (0 or more)");
  }

  return errors;
}
